// @flow strict
import React from 'react'

import styles from './DefaultFooter.module.scss'
import { ImgLink } from '../Header/Header'

import appStoreBadgeImg from '../../static/app-store-badge-black.svg'
import playStoreBadgeImg from '../../static/play-store-badge-black.svg'

type AltLinks = {|
  termsAndConditions?: {
    label: string,
    href: string,
  },
  privacyPolicy?: {
    label: string,
    href: string,
  },
  informationAboutAccount?: {
    label: string,
    href: string,
  },
|}

const defaultAltLinks: AltLinks = {
  termsAndConditions: {
    label: 'Terms & Conditions',
    href: '/legal/terms-and-conditions/',
  },
  privacyPolicy: {
    label: 'Privacy Notice',
    href: '/legal/privacy-notice/',
  },
  informationAboutAccount: {
    label: 'Information about our Personal Current Account Services',
    href: '/information-about-current-account-services/',
  },
}

const AppleAppStoreBadge = () => (
  <ImgLink
    className={styles.appStoreBadgeLink}
    href="https://app.adjust.com/ydi27sn?engagement_type=fallback_click"
    title="External link to the iOS app store"
    src={appStoreBadgeImg}
    alt="Download the Monzo app on iOS"
    imgStyle={{}}
  />
)

const GooglePlayStoreBadge = () => (
  <ImgLink
    className={styles.appStoreBadgeLink}
    href="https://app.adjust.com/9mq4ox7?engagement_type=fallback_click"
    title="External link to the Google play store"
    src={playStoreBadgeImg}
    alt="Download the Monzo app on Android"
    imgStyle={{}}
  />
)

const SocialNav = () => (
  <nav className={styles.socialNavIcons}>
    <ImgLink
      className={styles.socialNav}
      title=""
      imgStyle={{}}
      href="https://twitter.com/monzo"
      src="https://d33wubrfki0l68.cloudfront.net/7c4833acc25609b5b10eb8fa93eda189c6499943/375bf/static/images/twitter.svg"
      alt="Twitter"
    />
    <ImgLink
      className={styles.socialNav}
      title=""
      href="https://www.instagram.com/monzo/"
      src="https://d33wubrfki0l68.cloudfront.net/54eee2417052a64260fd4b51a3095d2585b326bf/e5287/static/images/social-icon-instagram.svg"
      width="40"
      alt="Instagram"
    />
    <ImgLink
      className={styles.socialNav}
      title=""
      imgStyle={{}}
      href="https://www.facebook.com/monzobank"
      src="https://d33wubrfki0l68.cloudfront.net/78c691dfe4746cb8a7c995d185d9ae2303a81eb2/efc18/static/images/facebook.svg"
      alt="Facebook"
    />
    <ImgLink
      className={styles.socialNav}
      title=""
      imgStyle={{}}
      href="https://www.linkedin.com/company/monzo-bank"
      src="https://d33wubrfki0l68.cloudfront.net/736e5ecac35e0ff72b923877cfffef4abf57edce/73171/static/images/linkedin.svg"
      alt="LinkedIn"
    />
    <ImgLink
      className={styles.socialNav}
      title=""
      imgStyle={{}}
      href="https://www.youtube.com/monzobank"
      src="https://d33wubrfki0l68.cloudfront.net/35aba7d7de6c8cb76e23f3ea072cdda077a9ecf0/434bf/static/images/youtube.svg"
      alt="Youtube"
    />
  </nav>
)

const SiteNav = ({ altLinks }) => {
  const {
    privacyPolicy = defaultAltLinks.privacyPolicy,
    termsAndConditions = defaultAltLinks.termsAndConditions,
    informationAboutAccount = defaultAltLinks.informationAboutAccount,
  } = altLinks

  return (
    <div className={styles.siteNav}>
      <ul>
        <li>
          <a href="/about/">About us</a>
        </li>
        <li>
          <a href="/blog/" title="Go to Blog" aria-label="Go to Blog">
            Blog
          </a>
        </li>
        <li>
          <a href="/press/">Press</a>
        </li>
        <li>
          <a href="/careers/">Careers</a>
        </li>
        <li>
          <a href="/i/our-social-programme/">Our social programme</a>
        </li>
        <li>
          <a href="/i/accessibility/">Accessibility</a>
        </li>
        <li>
          <a href="https://web.monzo.com">
            {'Lost your phone '}
            <br />
            and card?
          </a>
        </li>
      </ul>
      <ul>
        <li>
          <a href="/i/supporting-all-our-customers/">
            Supporting all our customers
          </a>
        </li>
        <li>
          <a href="/i/helping-everyone-belong-at-monzo/">
            Helping everyone belong at Monzo
          </a>
        </li>
        <li>
          <a href="/i/coronavirus-update/">Coronavirus update</a>
        </li>
        <li>
          <a href="/i/fraud/">How we protect you</a>
        </li>
        <li>
          <a href="/tone-of-voice/">Our tone of voice</a>
        </li>
        <li>
          <a href="/i/business/">Business Accounts</a>
        </li>
        <li>
          <a href="/static/docs/modern-slavery-statement/modern-slavery-statement-2020.pdf">
            Modern Slavery Statement
          </a>
        </li>
      </ul>
      <ul>
        <li>
          <a href="/faq/" aria-label="Frequently Asked Questions">
            FAQ
          </a>
        </li>
        {termsAndConditions && (
          <li>
            <a href={termsAndConditions.href}>{termsAndConditions.label}</a>
          </li>
        )}
        {privacyPolicy && (
          <li>
            <a href={privacyPolicy.href}>{privacyPolicy.label}</a>
          </li>
        )}
        <li>
          <a href="/legal/fscs-information/">FSCS information</a>
        </li>
        <li>
          <a href="/legal/cookie-notice/">Cookie Notice</a>
        </li>
        {informationAboutAccount && (
          <li>
            <a href={informationAboutAccount.href}>
              {informationAboutAccount.label}
            </a>
          </li>
        )}

        <li>
          <a href="/service-information/">App and Open Banking Performance</a>
        </li>
      </ul>
    </div>
  )
}

export default ({ altLinks = defaultAltLinks }: { altLinks?: AltLinks }) => (
  <footer className={styles.footerWrapper}>
    <div className={styles.contentContainer}>
      <div className={styles.flexContainer}>
        <div className={styles.logoContainer}>
          <ImgLink
            href="/"
            title="Monzo homepage"
            src="https://d33wubrfki0l68.cloudfront.net/673084cc885831461ab2cdd1151ad577cda6a49a/92a4d/static/images/favicon.png"
            alt="Monzo Logo"
          />
        </div>
        <SiteNav altLinks={altLinks} />
        <div
          data-testid="AppStoreAndSocialBadges"
          className={styles.appStoreAndSocialBadges}
        >
          <AppleAppStoreBadge />
          <GooglePlayStoreBadge />
          <SocialNav />
        </div>
      </div>
    </div>
    <div className={styles.disclaimer}>
      <div className={styles.disclaimerContentContainer}>
        <p className={styles.help}>
          {'Existing customers can get help via the app'}
        </p>
        <p>
          Monzo Bank Limited is a company registered in England and Wales (No.
          09446231). Monzo Bank Ltd is authorised by the Prudential Regulation
          Authority (PRA) and regulated by the Financial Conduct Authority and
          the PRA. Our Financial Services Register number is 730427.
        </p>
      </div>
    </div>
  </footer>
)
